import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createReportCustomerPreset,
  deleteReportCustomerPreset,
  getReportCustomerPresets,
  updateReportCustomerPreset,
} from '../api/client';
import type { ReportCustomerPreset } from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  SHARP_DECLINE_GROWTH_MAX,
  SHARP_DECLINE_OLD_DEBT_MIN,
  columnFiltersFromPreset,
  defaultTrackedItemFilters,
  hasInvalidPresetRange,
  hasPresetCondition,
  presetFiltersFromColumnFilters,
} from '../lib/reportCustomerPresets';
import type { TrackedItemColumnFilters } from '../lib/reportCustomerPresets';

interface Props {
  filters: TrackedItemColumnFilters;
  activePresetId: string;
  onApply: (presetId: string, filters: TrackedItemColumnFilters, strictBuiltIn: boolean) => void;
  onReset: () => void;
}

const pairFields: Array<{
  labelKey: string;
  minimum: keyof TrackedItemColumnFilters;
  maximum: keyof TrackedItemColumnFilters;
  integer?: boolean;
}> = [
  { labelKey: 'reports.trackedItemQuantity', minimum: 'quantityMin', maximum: 'quantityMax' },
  { labelKey: 'reports.oldDebt', minimum: 'oldDebtMin', maximum: 'oldDebtMax' },
  { labelKey: 'reports.totalDebt', minimum: 'totalDebtMin', maximum: 'totalDebtMax' },
  { labelKey: 'reports.avgMonthly3m', minimum: 'avg3mMin', maximum: 'avg3mMax' },
  { labelKey: 'reports.avgMonthly6m', minimum: 'avg6mMin', maximum: 'avg6mMax' },
  { labelKey: 'reports.growthIndicator', minimum: 'growthMin', maximum: 'growthMax' },
  { labelKey: 'reports.filterNextOrder', minimum: 'nextOrderMin', maximum: 'nextOrderMax', integer: true },
];

function errorDetail(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : null;
}

export default function ReportCustomerPresetControls({
  filters,
  activePresetId,
  onApply,
  onReset,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [presets, setPresets] = useState<ReportCustomerPreset[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [editingPreset, setEditingPreset] = useState<ReportCustomerPreset | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftFilters, setDraftFilters] = useState<TrackedItemColumnFilters>(defaultTrackedItemFilters);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [menuPresetId, setMenuPresetId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReportCustomerPreset | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReportCustomerPresets()
      .then((items) => {
        if (!cancelled) setPresets(items);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canManage = (preset: ReportCustomerPreset) => (
    user?.role === 'owner' || user?.role === 'admin' || preset.created_by === user?.id
  );

  const openCreate = () => {
    setEditingPreset(null);
    setDraftName('');
    setDraftFilters({ ...filters });
    setSaveError(null);
    setModalOpen(true);
  };

  const openEdit = (preset: ReportCustomerPreset) => {
    setEditingPreset(preset);
    setDraftName(preset.name);
    setDraftFilters(columnFiltersFromPreset(preset.filters));
    setSaveError(null);
    setMenuPresetId(null);
    setModalOpen(true);
  };

  const updateDraft = (key: keyof TrackedItemColumnFilters, value: string) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const savePreset = async () => {
    const name = draftName.trim();
    const presetFilters = presetFiltersFromColumnFilters(draftFilters);
    if (!name) {
      setSaveError(t('reports.presetNameRequired'));
      return;
    }
    if (!hasPresetCondition(presetFilters)) {
      setSaveError(t('reports.presetConditionRequired'));
      return;
    }
    if (hasInvalidPresetRange(presetFilters)) {
      setSaveError(t('reports.presetRangeInvalid'));
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const saved = editingPreset
        ? await updateReportCustomerPreset(editingPreset.id, { name, filters: presetFilters })
        : await createReportCustomerPreset({ name, filters: presetFilters });
      setPresets((current) => (
        [...current.filter((preset) => preset.id !== saved.id), saved]
          .sort((a, b) => a.name.localeCompare(b.name))
      ));
      setModalOpen(false);
      setEditingPreset(null);
      onApply(saved.id, columnFiltersFromPreset(saved.filters), false);
    } catch (error: unknown) {
      setSaveError(
        axios.isAxiosError(error) && error.response?.status === 409
          ? t('reports.presetDuplicateName')
          : (errorDetail(error) || t('reports.presetSaveError')),
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteReportCustomerPreset(deleteTarget.id);
      setPresets((current) => current.filter((preset) => preset.id !== deleteTarget.id));
      if (activePresetId === deleteTarget.id) onReset();
      setDeleteTarget(null);
    } catch {
      setSaveError(t('reports.presetDeleteError'));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const applyBuiltIn = () => {
    onApply('builtin-sharp-decline', {
      ...defaultTrackedItemFilters,
      oldDebtMin: String(SHARP_DECLINE_OLD_DEBT_MIN),
      growthMax: String(SHARP_DECLINE_GROWTH_MAX),
    }, true);
  };

  const chipClass = (active: boolean) => (
    `rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
      active
        ? 'border-blue-600 bg-blue-600 text-white'
        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
    }`
  );

  return (
    <>
      <div className="mb-4" data-testid="report-customer-presets">
        <div className="flex flex-wrap items-center gap-2">
          <span className="me-1 text-sm font-medium text-gray-600">{t('reports.presets')}:</span>
          <button
            type="button"
            aria-pressed={activePresetId === 'all'}
            onClick={onReset}
            className={chipClass(activePresetId === 'all')}
          >
            {t('reports.presetAllCustomers')}
          </button>
          <button
            type="button"
            aria-pressed={activePresetId === 'builtin-sharp-decline'}
            title={t('reports.presetSharpDeclineTooltip')}
            onClick={applyBuiltIn}
            className={chipClass(activePresetId === 'builtin-sharp-decline')}
          >
            {t('reports.presetSharpDecline')}
          </button>
          {presets.map((preset) => (
            <div key={preset.id} className="relative inline-flex">
              <button
                type="button"
                aria-pressed={activePresetId === preset.id}
                onClick={() => onApply(preset.id, columnFiltersFromPreset(preset.filters), false)}
                className={`${chipClass(activePresetId === preset.id)} ${canManage(preset) ? 'rounded-e-none border-e-0' : ''}`}
              >
                {preset.name}
              </button>
              {canManage(preset) && (
                <button
                  type="button"
                  aria-label={t('reports.presetManage', { name: preset.name })}
                  aria-expanded={menuPresetId === preset.id}
                  onClick={() => setMenuPresetId((current) => current === preset.id ? null : preset.id)}
                  className={`rounded-e-full border px-2 py-1.5 text-sm ${
                    activePresetId === preset.id
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600'
                  }`}
                >
                  ⋯
                </button>
              )}
              {menuPresetId === preset.id && canManage(preset) && (
                <div className="absolute end-0 top-full z-20 mt-1 min-w-32 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <button type="button" onClick={() => openEdit(preset)} className="block w-full px-3 py-2 text-start text-sm text-gray-700 hover:bg-gray-50">
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteTarget(preset);
                      setMenuPresetId(null);
                    }}
                    className="block w-full px-3 py-2 text-start text-sm text-red-600 hover:bg-red-50"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              )}
            </div>
          ))}
          <button type="button" onClick={openCreate} className="rounded-full border border-dashed border-blue-400 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50">
            + {t('reports.newPreset')}
          </button>
        </div>
        {loadError && <p className="mt-2 text-xs text-amber-700">{t('reports.presetsLoadError')}</p>}
        {saveError && !modalOpen && <p className="mt-2 text-xs text-red-600">{saveError}</p>}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="report-preset-title">
            <div className="mb-4 flex items-center justify-between gap-4">
              <h2 id="report-preset-title" className="text-lg font-semibold text-gray-900">
                {t(editingPreset ? 'reports.editPreset' : 'reports.createPreset')}
              </h2>
              <button type="button" onClick={() => setModalOpen(false)} className="text-2xl text-gray-400 hover:text-gray-600" aria-label={t('reports.close')}>×</button>
            </div>
            <p className="mb-4 text-sm text-gray-500">{t('reports.presetModalHint')}</p>
            <label className="mb-4 flex flex-col gap-1 text-sm font-medium text-gray-700">
              {t('reports.presetName')}
              <input
                type="text"
                maxLength={80}
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 font-normal focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                {t('reports.customerName')}
                <input
                  type="text"
                  value={draftFilters.customer}
                  onChange={(event) => updateDraft('customer', event.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 font-normal"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                {t('reports.filterItemGroup')}
                <input
                  type="text"
                  value={draftFilters.itemGroup}
                  onChange={(event) => updateDraft('itemGroup', event.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 font-normal"
                />
              </label>
              {pairFields.map((field) => (
                <fieldset key={field.labelKey} className="rounded-lg border border-gray-200 p-3">
                  <legend className="px-1 text-sm font-medium text-gray-700">{t(field.labelKey)}</legend>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      step={field.integer ? 1 : 'any'}
                      value={draftFilters[field.minimum]}
                      onChange={(event) => updateDraft(field.minimum, event.target.value)}
                      placeholder={t('reports.filterMin')}
                      aria-label={`${t(field.labelKey)} ${t('reports.filterMin')}`}
                      className="min-w-0 rounded-lg border border-gray-300 px-3 py-2"
                    />
                    <input
                      type="number"
                      step={field.integer ? 1 : 'any'}
                      value={draftFilters[field.maximum]}
                      onChange={(event) => updateDraft(field.maximum, event.target.value)}
                      placeholder={t('reports.filterMax')}
                      aria-label={`${t(field.labelKey)} ${t('reports.filterMax')}`}
                      className="min-w-0 rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>
                </fieldset>
              ))}
              <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                {t('reports.presetGrowthStatus')}
                <select
                  value={draftFilters.growthStatus}
                  onChange={(event) => updateDraft('growthStatus', event.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 font-normal"
                >
                  <option value="all">{t('reports.filterAllGrowth')}</option>
                  <option value="growing">{t('reports.growth.growing')}</option>
                  <option value="stable">{t('reports.growth.stable')}</option>
                  <option value="struggling">{t('reports.growth.struggling')}</option>
                </select>
              </label>
            </div>
            {saveError && <p className="mt-3 text-sm text-red-600" role="alert">{saveError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={savePreset} disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                {saving ? t('reports.presetSaving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" role="alertdialog" aria-modal="true" aria-labelledby="delete-report-preset-title">
            <h2 id="delete-report-preset-title" className="text-lg font-semibold text-gray-900">{t('reports.deletePresetTitle')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('reports.deletePresetBody', { name: deleteTarget.name })}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={confirmDelete} disabled={deleting} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                {deleting ? t('reports.presetDeleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
